/**
 * Committing one cell, wherever it was edited.
 *
 * The grid and the row modal open the same editors and have to answer the same
 * question afterwards -- is this worth sending -- so they ask it here. Written
 * twice they would drift, and the drift would be invisible: both would work,
 * and one of them would quietly write on every open and close.
 */
import type { Field, RowData } from '@/api/types'
import { fieldKey } from '@/api/types'
import type { FieldContext } from '../fields/behavior/behavior'
import { editorFor } from '../fields/ui/edit'
import { cellValue } from '../fields/read'
import { updateRowValues, type UpdateRowOptions } from './rowMutations'
import type { RowWindowMember } from './rowWindowRegistry'

/**
 * Sends what was typed into one cell, or nothing when it changes nothing.
 *
 * Null means there was nothing to send, which is a different answer from a
 * request that failed -- the caller has an error to report for the second and
 * nothing to do about the first.
 *
 * What is compared is what an *untouched* cell would send, not the value the
 * row holds. Those are the same thing for a text field and are not for most
 * others: a select cell holds an option and sends an option, a date cell holds
 * seconds its format never shows. Running the stored value out through the
 * same pair of functions asks the question that actually matters -- would
 * sending this change anything -- and answers it once for every type.
 *
 * Compared as what would be *sent*, rather than by identity: a multiple select
 * sends a list, and two lists naming the same options are two different
 * objects. Two values that serialise the same way are the same request.
 */
export function commitCellEdit(
  member: RowWindowMember,
  row: RowData,
  field: Field,
  draft: string,
  context: FieldContext,
  options: UpdateRowOptions = {},
): Promise<void> | null {
  const editor = editorFor(field)
  if (editor === null) return null

  const next = editor.fromDraft(field, draft, context)
  const unchanged = editor.fromDraft(
    field,
    editor.toDraft(field, cellValue(field, row)),
    context,
  )
  if (JSON.stringify(unchanged) === JSON.stringify(next)) return null

  return updateRowValues(member, row.id, { [fieldKey(field.id)]: next }, options)
}
